import {DomOrderStrategy, LayoutIntentType, type Pointer, type Rect} from '@wix/document-services-types'
import {getMeasurementsPointer, getStructurePointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import type {ConversionDal} from '../../types'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import type {LayoutIntent} from '../../conversionDal/types'

export const sortHeaderContainerChildren = (conversionDal: ConversionDal) => (childIdA: string, childIdB: string) => {
    const getSemanticType = (id: string) => conversionDal.get(getStructurePointer({id}))?.semanticType as SemanticTypes

    const getWidth = (id: string) => conversionDal.get(getMeasurementsPointer({id}))?.width ?? 0

    const isFirstRow = (id: string) => {
        const layout = conversionDal.get(getTargetLayoutPointer({id}))
        const {itemLayout} = layout ?? {}
        return itemLayout?.gridArea?.rowStart === 1
    }

    const semanticTypeA = getSemanticType(childIdA)
    const semanticTypeB = getSemanticType(childIdB)

    const isFirstRowA = isFirstRow(childIdA)
    const isFirstRowB = isFirstRow(childIdB)

    // Prioritize row 1 components over others
    if (isFirstRowA && !isFirstRowB) return -1
    if (!isFirstRowA && isFirstRowB) return 1

    // Menu is always first
    if (semanticTypeA === SemanticTypes.MENU) return -1
    if (semanticTypeB === SemanticTypes.MENU) return 1

    // Logo is always last
    if (semanticTypeA === SemanticTypes.LOGO) return 1
    if (semanticTypeB === SemanticTypes.LOGO) return -1

    // For other components, order by width (smaller width first)
    return getWidth(childIdA) - getWidth(childIdB)
}

const compareByXThenY = (rectA: Rect, rectB: Rect) => {
    return rectA!.x - rectB!.x || rectA!.y - rectB!.y
}

const compareByXThenYRtl = (rectA: Rect, rectB: Rect) => {
    return rectB!.x - rectA!.x || rectA!.y - rectB!.y
}

const compareByYThenX = (rectA: Rect, rectB: Rect) => {
    return rectA!.y - rectB!.y || rectA!.x - rectB!.x
}

const compareByYThenXRtl = (rectA: Rect, rectB: Rect) => {
    return rectA!.y - rectB!.y || rectB!.x - rectA!.x
}

export const compareByNOrder = (isRtl: boolean, rectA: Rect, rectB: Rect) => {
    return isRtl ? compareByXThenYRtl(rectA, rectB) : compareByXThenY(rectA, rectB)
}

export const compareByZOrder = (isRtl: boolean, rectA: Rect, rectB: Rect) => {
    return isRtl ? compareByYThenXRtl(rectA, rectB) : compareByYThenX(rectA, rectB)
}

export type CompareFunction = (sortValueA: any, sortValueB: any) => number

export const getOrderStrategy = (layoutIntent: LayoutIntent | undefined, isRtl: boolean) => {
    const isNOrder =
        layoutIntent?.type === LayoutIntentType.SuperGrid && layoutIntent?.domOrderStrategy === DomOrderStrategy.NShape

    const orderStrategy = isNOrder ? 'N-ORDER' : 'Z-ORDER'
    const direction = isRtl ? 'RTL' : 'LTR'
    const compareFunction: CompareFunction = isNOrder
        ? (rectA, rectB) => compareByNOrder(isRtl, rectA, rectB)
        : (rectA, rectB) => compareByZOrder(isRtl, rectA, rectB)

    return {
        appliedHeuristics: [`${orderStrategy} (${direction})`],
        compareFunction
    }
}

export const generatePinnedChildLayout = () => ({
    itemLayout: {
        type: 'GridItemLayout',
        alignSelf: 'start',
        justifySelf: 'start',
        gridArea: {
            rowStart: -1,
            rowEnd: 1,
            columnStart: -1,
            columnEnd: 1
        },
        pinnedToContainer: true
    }
})

export const generateChildLayout = (startRow: number, endColumn: number = 2) => ({
    itemLayout: {
        type: 'GridItemLayout',
        alignSelf: 'start',
        justifySelf: 'start',
        gridArea: {
            rowStart: startRow,
            rowEnd: startRow + 1,
            columnStart: 1,
            columnEnd: endColumn
        }
    }
})

export const getContainerRowCountKey = (containerPointer: Pointer): string => {
    return `containerRowCount_${containerPointer.id}`
}
